import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import Transition from "../mosaic/utils/Transition.jsx";
import Button from "./Button.jsx";

function Dropdown({align, title, button, className = "", contentClassName = "", children, inhibitClosing, showTitle = true, type = "default", portal = false, portalBoundarySelector = null}) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(null);
    const [portalPosition, setPortalPosition] = useState(null);

    const trigger = useRef(null);
    const dropdown = useRef(null);

    inhibitClosing = inhibitClosing ?? false;

    const isModernType = type === "modern";
    const dropdownClassName = `origin-top-right ${portal ? "z-50 fixed" : "z-10 absolute top-full"} left-0 right-auto min-w-56 overscroll-contain overflow-x-hidden overflow-y-auto mt-1 ${
        isModernType
            ? "dropdown-container-wrap rounded-xl border border-[#b6bdc8] dark:border-gray-500 bg-white shadow-lg mt-1.5"
            : "rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pt-1.5 shadow-lg"
    } ${
        align === 'right' ? 'md:left-auto md:right-0' : 'md:left-0 md:right-auto'
    }`;
    const dropdownContentClassName = `${isModernType ? '' : 'px-3 pb-2'} ${contentClassName}`.trim();
    const titleClassName = `text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase ${
        isModernType ? '' : 'pt-1.5'
    } pb-2`;

    useEffect(() => {
        if (!dropdownOpen || !trigger.current) {
            return undefined;
        }

        const updateDropdownBounds = () => {
            const triggerBounds = trigger.current.getBoundingClientRect();
            const {bottom} = triggerBounds;
            setDropdownMaxHeight(Math.max(0, window.innerHeight - bottom - 12));

            if (!portal) {
                return;
            }

            const boundary = portalBoundarySelector
                ? document.querySelector(portalBoundarySelector)?.getBoundingClientRect()
                : null;
            const boundaryLeft = (boundary?.left ?? 0) + 12;
            const boundaryRight = (boundary?.right ?? window.innerWidth) - 12;
            const availableWidth = Math.max(0, boundaryRight - boundaryLeft);
            const contentWidth = dropdown.current?.scrollWidth ?? 0;
            const container = dropdown.current?.parentElement;
            const containerChromeWidth = container
                ? container.offsetWidth - container.clientWidth
                : 0;
            const width = Math.min(
                contentWidth ? contentWidth + containerChromeWidth : availableWidth,
                availableWidth,
            );
            const preferredLeft = align === "right"
                ? triggerBounds.right - width
                : triggerBounds.left;
            const left = Math.min(Math.max(preferredLeft, boundaryLeft), boundaryRight - width);

            setPortalPosition({
                top: bottom + 6,
                left,
                width,
                maxWidth: availableWidth,
            });
        };

        const frame = requestAnimationFrame(updateDropdownBounds);
        window.addEventListener('resize', updateDropdownBounds);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateDropdownBounds);
        };
    }, [align, dropdownOpen, portal, portalBoundarySelector]);

    // close on click outside
    useEffect(() => {
        const clickHandler = ({target}) => {
            if ((inhibitClosing ?? false) === true) return;
            if (!dropdown.current) return;
            if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
            setDropdownOpen(false);
        };
        document.addEventListener('click', clickHandler);
        return () => document.removeEventListener('click', clickHandler);
    });

    // close if the esc key is pressed
    useEffect(() => {
        const keyHandler = ({keyCode}) => {
            if (!dropdownOpen || keyCode !== 27) return;
            setDropdownOpen(false);
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    });

    const dropdownElement = (
        <Transition
            show={dropdownOpen}
            tag="div"
            className={dropdownClassName}
            enter="transition ease-out duration-200 transform"
            enterStart="opacity-0 -translate-y-2"
            enterEnd="opacity-100 translate-y-0"
            leave="transition ease-out duration-200"
            leaveStart="opacity-100"
            leaveEnd="opacity-0"
            style={{
                ...(dropdownMaxHeight === null ? {} : {maxHeight: `${dropdownMaxHeight}px`}),
                ...(portalPosition === null ? {} : portalPosition),
            }}
        >
            <div ref={dropdown} className={dropdownContentClassName}>
                {showTitle &&
                    <div className={titleClassName}>
                        {title}
                    </div>
                }
                {children}
            </div>
        </Transition>
    );

    return (
        <div className={`filter-dropdown-trigger relative inline-flex ${className}`}>
            <Button
                ref={trigger}
                variant={"outline-black"}
                aria-haspopup="true"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
            >
                {button ?? title}
            </Button>
            {portal ? createPortal(dropdownElement, document.body) : dropdownElement}
        </div>
    );
}

export default Dropdown;
